import { observer } from "mobx-react";
import React, { useMemo, useRef, useState } from "react";
import theme from "Theme/AppTheme";
import AcxVerticalBarGraph from "components/UI/Visualization/AcxVerticalBarGraph";
import AcxLineGraph from "components/UI/Visualization/AcxLineGraph";
import AcxHorizontalBarGraph from "components/UI/Visualization/AcxHorizontalBarGraph";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxDonutGraph from "components/UI/Visualization/AcxDonutGraph";
import AcxTreeMap, {
    TreeMapSelectedNode,
} from "components/UI/Visualization/AcxTreeMap";
import { Palettes } from "components/UI/Visualization/GraphColorSchemes";
import {
    signalDataToTreeMapFormat,
    TREE_MAP_SYNTHETIC_ROOT_ID,
    XValueType,
} from "utils/ReportDataToGoogleFormat";
import ChartCardStore from "./ChartCardStore";
import { useChartFontsReady } from "./chartFonts";
import {
    RenderModeType,
    rowHasChildren,
} from "components/Signals/SignalsModels";
import PointMenu from "../Drilldown/PointMenu";
import DrilldownDialog from "../Drilldown/DrilldownDialog";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { resolveDataAvailability } from "components/Signals/DataAvailability/DataAvailability";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";

const Chart = observer(({ store }: { store: ChartCardStore }) => {
    const authStore = useStore(AuthStore);
    const fontsReady = useChartFontsReady();
    const [pointMenuOpen, setPointMenuOpen] = useState(false);

    // Ref to store the mouse position for anchoring the point menu
    const mousePosition = useRef<{ x: number | null; y: number | null }>({
        x: 0,
        y: 0,
    });

    // Event handler to update the ref's current value
    const handleMouseMove = (event) => {
        mousePosition.current = { x: event.clientX, y: event.clientY };
    };

    React.useEffect(() => {
        // Attach mousemove listener
        window.addEventListener("mousemove", handleMouseMove);

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    const vAxisDefaultTextStyle = {
        bold: false,
        italic: false,
        fontSize: "12",
        color: theme.palette.grey[600],
    };

    const vAxistDefaultTitleStyle = {
        italic: false,
        bold: false,
        fontSize: "12",
        color: theme.palette.grey[700],
    };

    const groupingActive = store.groupingStore.isGrouped;
    const isStacked = store.config.renderOptions.forceStacked || groupingActive;

    const dataForScale = store.chartDataForScale;

    // Highest the vertical or horizontal axis will go for the specified data set
    const maximumAxisValue: number | undefined = useMemo(() => {
        const dataWithoutHeader = dataForScale
            ? dataForScale.slice(1)
            : undefined;
        if (!dataWithoutHeader || dataWithoutHeader.length === 0)
            return undefined;
        let max = 0;
        dataWithoutHeader.forEach((row) => {
            let rowMax = 0;
            // Graphs have data at odd(1,3,5,7...) positions
            switch (store.chartType) {
                // Line graphs can have many values per row
                // We need to get the max of all values in the row
                case "Line":
                    let lineRowMax = 0;
                    row.forEach((value, index) => {
                        if (index % 2 === 1 && value > lineRowMax) {
                            lineRowMax = value as number;
                        }
                    });
                    rowMax = lineRowMax;
                    break;
                // Bar graphs can have 1 or more values, stacked or unstacked
                case "VerticalBar":
                case "HorizontalBar":
                    if (isStacked) {
                        // Since these are stacked, add all values together
                        let stackedTotal = 0;
                        row.forEach((value, index) => {
                            if (index % 2 === 1) {
                                stackedTotal += value as number;
                            }
                        });
                        rowMax = stackedTotal;
                    } else {
                        // Not stacked, just take the highest value
                        let unstackedRowMax = 0;
                        row.forEach((value, index) => {
                            if (index % 2 === 1 && value > unstackedRowMax) {
                                unstackedRowMax = value as number;
                            }
                        });
                        rowMax = unstackedRowMax;
                    }
                    break;
                default:
                // Other chart types don't have pagination so there's no need to set the scale
            }

            if (rowMax > max) max = rowMax;
        });
        return max;
    }, [dataForScale, store.chartType, isStacked]);

    const chartOnSelect = ({
        series,
        x,
        y,
        seriesIdentifier,
    }: {
        series: string | null;
        x: XValueType | null;
        y: any;
        seriesIdentifier?: string | null;
    }) => {
        if (!store.config.capabilities.pointSelectMenu || !store.drilldownStore)
            return;

        if (
            store.isPresentationMode ||
            (!x && !y && !seriesIdentifier) // disable onClick menu for clicks on legend
        )
            return;

        // Clear any previous selection
        store.drilldownStore.clearSelection();

        let count = y;

        if (store.currentRenderMode === RenderModeType.Percent) {
            let countResolved = false;
            if (store.config.kboUseYPercentTotal) {
                const rawDatum = store.getRawDatumForPoint(
                    x?.v ?? undefined,
                    // Use `||` (not `??`) so an empty-string identifier — emitted
                    // for series that have no grouping_identifier, e.g. the eddy
                    // "Eddy Identified"/"Eddy Not Identified" split — falls back to
                    // the series label, which getRawDatumForPoint matches on
                    // `grouping`. `??` would keep the empty string and fail to match.
                    seriesIdentifier || series,
                );

                if (rawDatum?.y_percenttotal != null) {
                    count = rawDatum.y_percenttotal;
                    countResolved = true;
                }
            }
            if (
                !countResolved &&
                store.countData &&
                !store.config.kboUseYPercentTotal
            ) {
                const countIndex = store.countData[0].findIndex((datum) => {
                    if (datum instanceof Object && "label" in datum) {
                        return datum.label === series;
                    }
                    return false;
                });
                store.countData.find((dataPoint) => {
                    const dataPointX = dataPoint[0];
                    if (dataPointX instanceof Object && "v" in dataPointX) {
                        const dataPointXIdentifier = dataPointX.v?.toString();
                        const xIdentifier = x?.v?.toString();
                        if (dataPointXIdentifier === xIdentifier) {
                            count = dataPoint[
                                countIndex > 0 ? countIndex : 1
                            ] as number;
                            return true;
                        }
                    }
                    return false;
                });
            }
        }

        store.drilldownStore.selectDataPoint({
            series,
            x,
            y,
            seriesIdentifier,
            count,
        });
        setPointMenuOpen(true);
    };

    // TreeMap reads `displayedData` (base when not zoomed, zoomStore data
    // when zoomed) and doesn't produce countData / percentData. The non-
    // TreeMap cases below coalesce `countData` / `percentData` with
    // `?? undefined` to satisfy their prop types — TS can't carry a single
    // narrowing across this ternary, so the case bodies fall back instead.
    const isValidData = store.hasRenderableChartData;
    const dataAvailability = resolveDataAvailability({
        authStore,
        policy: store.config.dataAvailability,
        defaultEmptyStateVariant: store.config.renderOptions.emptyStateVariant,
        startDate: store.state?.startDate,
        endDate: store.state?.endDate,
        hasRenderableData: isValidData,
    });
    const hasSyntheticTemporalGaps =
        store.chartType === "Line" &&
        (store.data?.some((item) => item.isSyntheticTemporalGap) ?? false);
    const disableAnimations =
        store.config.capabilities.comparePrev &&
        (store.comparePrevEnabled ||
            store.comparePrevLoading ||
            !!store.comparePrevData);

    const legendVisibility = store.config.renderOptions.legendVisibility;
    const barLegendExplicitlyHidden = legendVisibility === "none";

    // Bar charts show a legend when their data needs one unless the endpoint
    // explicitly hides it.
    const forceBarLegend =
        !barLegendExplicitlyHidden &&
        (groupingActive ||
            store.comparePrevEnabled ||
            isStacked ||
            legendVisibility === "bottom");
    const barLegendPosition = forceBarLegend ? "bottom" : "none";
    const lineLegendPosition =
        groupingActive || legendVisibility === "bottom" ? "bottom" : "none";
    const defaultGroupedPalette = groupingActive
        ? Palettes.signalsChartColorScheme
        : store.config.renderOptions.palette;
    const activeColorScheme = store.comparePrevEnabled
        ? store.config.renderOptions.comparePrevPalette
        : defaultGroupedPalette;
    let lineSeries: Record<string, unknown>[] | undefined;
    const hiddenSeriesIndexes = new Set<number>();
    if (store.visibleSeries && (store.seriesNames?.length ?? 0) > 0) {
        lineSeries = [];
        (store.seriesNames ?? []).forEach((seriesName, index) => {
            if (store.visibleSeries?.includes(seriesName)) {
                lineSeries?.push({});
            } else {
                lineSeries?.push({ lineWidth: 0, visibleInLegend: false });
                hiddenSeriesIndexes.add(index);
            }
        });
    }

    const chartData =
        store.currentRenderMode === RenderModeType.Count
            ? store.countData
            : store.percentData;
    // Blank hidden lines so they can't sit on top of a visible line and block its hover
    const lineData = chartData?.map((row, rowIndex) =>
        rowIndex === 0
            ? row
            : row.map((cell, column) =>
                  hiddenSeriesIndexes.has((column - 1) / 2) ? null : cell,
              ),
    );

    const renderChart = () => {
        if (!isValidData) return null;
        switch (store.chartType) {
            case "TreeMap": {
                const rows = store.displayedData ?? [];
                const zoomPath = store.zoomStore.zoomPath;
                // `displayedZoomPath` tracks the level the *currently
                // rendered* data was fetched at — it lags behind `zoomPath`
                // during an in-flight zoom so the color palette and header
                // template don't flip ahead of the cells they describe. The
                // breadcrumb still binds to the live `zoomPath`.
                const displayedZoomPath = store.zoomStore.displayedZoomPath;
                // The TreeMap primitive only sees one level at a time, so it
                // can't decide hasChildren by scanning its own data table — we
                // build a Set of cell ids whose metadata reports children and
                // pass it down.
                const nodesWithChildren = new Set<string>(
                    rows
                        .filter((r) => rowHasChildren(r.metadata))
                        .map((r) => r.x_identifier ?? r.x),
                );
                const treeMapData = signalDataToTreeMapFormat(
                    rows,
                    TREE_MAP_SYNTHETIC_ROOT_ID,
                );

                const viz = store.parsedVizOptions ?? {};
                const displayedDepth = displayedZoomPath.length;
                const palette =
                    viz.colors && viz.colors.length > 0
                        ? viz.colors
                        : store.config.renderOptions.palette ??
                          Palettes.signalsChartColorScheme;
                const levelBaseColor = palette[displayedDepth % palette.length];

                // Resolve `${N}` placeholders in headerTemplates against the
                // displayed zoom path; fall back to levelLabels[depth] or the
                // deepest entry's display name. Using displayed (not live)
                // keeps the header in sync with the data on screen.
                const template = viz.headerTemplates?.[displayedDepth];
                const headerLabel = template
                    ? template.replace(
                          /\$\{(\d+)\}/g,
                          (_match, idx) =>
                              displayedZoomPath[Number(idx)]?.x ?? "",
                      )
                    : viz.levelLabels?.[displayedDepth] ??
                      displayedZoomPath[displayedZoomPath.length - 1]?.x ??
                      "";

                const treeMapOnSelect = (node: TreeMapSelectedNode) => {
                    if (
                        !store.config.capabilities.pointSelectMenu ||
                        !store.drilldownStore ||
                        store.isPresentationMode ||
                        node.parent === null // skip the synthetic root row
                    ) {
                        return;
                    }
                    store.drilldownStore.clearSelection();
                    const matchedRow = rows.find(
                        (r) => (r.x_identifier ?? r.x) === node.id,
                    );
                    store.drilldownStore.selectTreeMapNode({
                        xIdentifier: matchedRow?.x_identifier,
                        xLabel: matchedRow?.x ?? node.id,
                        value: matchedRow?.y ?? node.value,
                        metadata: matchedRow?.metadata,
                    });
                    setPointMenuOpen(true);
                };

                return (
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            // Fills the plot area; the card enforces the floor.
                            flex: "1 1 auto",
                            minHeight: 0,
                        }}
                    >
                        {store.zoomStore.isFetching && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(255,255,255,0.6)",
                                    zIndex: 2,
                                }}
                            >
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        <AcxTreeMap
                            data={treeMapData}
                            zoomPath={zoomPath}
                            nodesWithChildren={nodesWithChildren}
                            levelBaseColor={levelBaseColor}
                            headerLabel={headerLabel}
                            onSelectNode={treeMapOnSelect}
                            onChartReady={(chartWrapper) =>
                                store.captureChartRef(chartWrapper)
                            }
                        />
                    </Box>
                );
            }
            case "Donut":
                return (
                    <AcxDonutGraph
                        data={
                            (store.currentRenderMode === RenderModeType.Count
                                ? store.fullCountData
                                : store.fullPercentData) ?? undefined
                        }
                        countData={store.fullCountData ?? undefined}
                        legendData={
                            (store.currentRenderMode === RenderModeType.Count
                                ? store.countData
                                : store.percentData) ?? undefined
                        }
                        chartId={store.id}
                        colorScheme={activeColorScheme}
                        showLegend={false} // using custom legend, not built in
                        // Any value but "none" shows the custom legend, which
                        // a compact donut cannot spare the width for.
                        legendPosition={
                            store.widthStore.isCompact ? "none" : "right"
                        }
                        showTotalCount
                        showAccuracy={store.config.renderOptions.showAccuracy}
                        onChartReady={(chartWrapper) =>
                            store.captureChartRef(chartWrapper)
                        }
                        onSelect={(
                            series,
                            x,
                            y,
                            _chartId,
                            seriesIdentifier,
                        ) => {
                            chartOnSelect({
                                series,
                                x,
                                y,
                                seriesIdentifier,
                            });
                        }}
                    />
                );
            case "VerticalBar":
                return (
                    <AcxVerticalBarGraph
                        chartAreaHeight={75}
                        chartAreaWidth="100%"
                        vAxisTextInside={store.widthStore.isCompact}
                        showGrid
                        legendPosition={barLegendPosition}
                        forceLegend={forceBarLegend}
                        data={
                            store.currentRenderMode === RenderModeType.Count
                                ? store.countData ?? undefined
                                : store.percentData ?? undefined
                        }
                        disableAnimation={disableAnimations}
                        yTitle={store.displayYTitle}
                        xTitle={store.xTitle}
                        verticalScaleType={store.scaleType}
                        vAxisFormat="short"
                        fontSize="1.25rem"
                        vAxisFontSize="12"
                        hAxisFontSize="12"
                        fontBold={false}
                        legendAlignment="center"
                        dataOpacity={1}
                        usesHtmlTooltips
                        slantedText={false}
                        colorScheme={activeColorScheme}
                        vAxisTitleStyle={vAxistDefaultTitleStyle}
                        vAxisTextStyle={vAxisDefaultTextStyle}
                        onChartReady={(chartWrapper) =>
                            store.captureChartRef(chartWrapper)
                        }
                        stacked={isStacked}
                        onSelect={(
                            series,
                            x,
                            y,
                            _chartId,
                            seriesIdentifier,
                        ) => {
                            chartOnSelect({
                                series,
                                x,
                                y,
                                seriesIdentifier,
                            });
                        }}
                        chartId={store.id}
                        vAxisMaxValue={maximumAxisValue}
                    />
                );

            case "Line":
                return (
                    <AcxLineGraph
                        showGrid
                        chartAreaHeight={75}
                        chartAreaWidth="100%"
                        vAxisTextInside={store.widthStore.isCompact}
                        shortDateLabels={store.widthStore.isCompact}
                        legendPosition={lineLegendPosition}
                        legendAlignment="center"
                        data={lineData}
                        yTitle={store.displayYTitle}
                        xTitle={store.xTitle}
                        verticalScaleType={store.scaleType}
                        vAxisFormat="short"
                        vAxisFontSize="12"
                        hAxisFontSize="12"
                        dataOpacity={1}
                        hideDataLabels
                        hideDataMarkers
                        usesHtmlTooltips
                        colorScheme={activeColorScheme}
                        vAxisTitleStyle={vAxistDefaultTitleStyle}
                        vAxisTextStyle={vAxisDefaultTextStyle}
                        onChartReady={(chartWrapper) =>
                            store.captureChartRef(chartWrapper)
                        }
                        series={lineSeries}
                        onSelect={(
                            series,
                            x,
                            y,
                            _chartId,
                            seriesIdentifier,
                        ) => {
                            chartOnSelect({
                                series,
                                x,
                                y,
                                seriesIdentifier,
                            });
                        }}
                        chartId={store.id}
                        temporalGrain={store.temporalGrain}
                        startDate={store.startDate}
                        endDate={store.endDate}
                        interpolateNulls={!hasSyntheticTemporalGaps}
                        vAxisMaxValue={maximumAxisValue}
                    />
                );
            case "HorizontalBar":
                return (
                    <AcxHorizontalBarGraph
                        chartAreaHeight={"80%"}
                        chartAreaWidth={
                            store.widthStore.horizontalBarChartAreaWidth
                        }
                        chartAreaLeftOffset={
                            store.widthStore.horizontalBarChartAreaLeftOffset
                        }
                        showGrid
                        legendPosition={barLegendPosition}
                        forceLegend={forceBarLegend}
                        data={
                            store.currentRenderMode === RenderModeType.Count
                                ? store.countData ?? undefined
                                : store.percentData ?? undefined
                        }
                        dataOpacity={1}
                        usesHtmlTooltips
                        colorScheme={activeColorScheme}
                        disableAnimation={disableAnimations}
                        stacked={isStacked}
                        xTitle={store.displayYTitle}
                        horizontalScaleType={
                            store.scaleType === "log" && isStacked
                                ? "mirrorLog"
                                : store.scaleType
                        }
                        vAxisTextStyle={vAxisDefaultTextStyle}
                        textPosition="out"
                        onChartReady={(chartWrapper) =>
                            store.captureChartRef(chartWrapper)
                        }
                        onSelect={(
                            series,
                            x,
                            y,
                            _chartId,
                            seriesIdentifier,
                        ) => {
                            chartOnSelect({
                                series,
                                x,
                                y,
                                seriesIdentifier,
                            });
                        }}
                        chartId={store.id}
                        hAxisFormat="0"
                        hAxisMaxValue={maximumAxisValue}
                    />
                );
            case "Table":
                return null;
        }
    };

    const currentlyLoadingChart = store.isChartDataLoading;
    const chartErrorPresent = store.isChartErrorPresent;

    const anchorPosition =
        mousePosition.current.y !== null && mousePosition.current.x !== null
            ? {
                  top: mousePosition.current.y,
                  left: mousePosition.current.x,
              }
            : undefined;

    const handlePointMenuClose = () => {
        setPointMenuOpen(false);
    };

    return (
        <>
            <PointMenu
                store={store}
                open={pointMenuOpen}
                anchorPosition={anchorPosition}
                onClose={handlePointMenuClose}
                isPercentage={
                    store.currentRenderMode === RenderModeType.Percent
                }
                isTemporal={store.config.renderOptions.isTemporal}
                temporalData={
                    store.config.renderOptions.isTemporal
                        ? {
                              temporalGrain: store.temporalGrain,
                              startDate: store.startDate,
                              endDate: store.endDate,
                          }
                        : undefined
                }
            />
            <DrilldownDialog
                baseObjectStore={store}
                drilldownStore={store.drilldownStore}
            />
            {currentlyLoadingChart && (
                <AcxEmptyState size="small" variant="loading" />
            )}
            {chartErrorPresent && (
                <AcxEmptyState size="small" variant="error" />
            )}
            {!currentlyLoadingChart &&
                !chartErrorPresent &&
                store.groupDeleted && (
                    <AcxEmptyState size="small" variant="groupDeleted" />
                )}
            {!currentlyLoadingChart &&
                !chartErrorPresent &&
                !store.groupDeleted &&
                store.groupAccessDenied && (
                    <AcxEmptyState size="small" variant="groupAccessDenied" />
                )}
            {fontsReady &&
                store.isDisplayingRenderableChartData &&
                renderChart()}
            {!currentlyLoadingChart &&
                !chartErrorPresent &&
                !store.groupDeleted &&
                !store.groupAccessDenied &&
                !isValidData && (
                    <AcxEmptyState
                        size="small"
                        variant={dataAvailability.emptyStateVariant}
                    />
                )}
        </>
    );
});

export default Chart;
